import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  User,
  Phone,
  Mail,
  Briefcase,
  CalendarDays,
  ShieldAlert,
  Save,
  Clock,
  FileText,
  Loader2,
  CircleDot,
} from 'lucide-react';
import { CREW_STATUSES, normalizeCrewMember } from '@/lib/crewRoster';
import { cn } from '@/lib/utils';

/* ----------------------------------------------------------------------------
 * Visual tone per status — controls the badge color + the inline pill in the
 * Settings → Crew Rates list. Kept consistent with the rest of the app's
 * tonal palette (success/warning/destructive HSL vars).
 * ------------------------------------------------------------------------- */
export const STATUS_TONE = {
  active: 'border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]',
  inactive: 'border-border bg-[hsl(var(--muted))] text-muted-foreground',
  laid_off: 'border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]',
  terminated: 'border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))]',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Status change side-effect — when the status moves to laid_off or
 * terminated the effective date is auto-stamped with today (the rule from
 * the spec). Moving back to active or inactive clears the effective date.
 *
 * Returns the patched member.
 */
export const applyStatusTransition = (member, nextStatus) => {
  const prev = member?.status || 'active';
  if (prev === nextStatus) return member;
  if (nextStatus === 'laid_off' || nextStatus === 'terminated') {
    return {
      ...member,
      status: nextStatus,
      status_effective_date: member?.status_effective_date || todayIso(),
    };
  }
  // active / inactive — clear the effective date so it doesn't linger.
  return { ...member, status: nextStatus, status_effective_date: '' };
};

/**
 * EmployeeProfileSheet — right-side drawer for editing the full employee
 * record. Renders inside Settings → Crew Rates when admin clicks "Edit"
 * on a row. Hosts the "Hours" and "Write-ups" entry points so that ALL
 * per-employee admin actions live in one screen.
 */
export const EmployeeProfileSheet = ({
  open,
  onOpenChange,
  member,
  onSave,
  onOpenHours,
  onOpenWriteups,
  saving = false,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(member || null);

  // Reset the draft every time the sheet is reopened on a new member.
  useEffect(() => {
    if (open) setDraft(normalizeCrewMember(member || {}));
  }, [open, member]);

  const isDirty = useMemo(() => {
    if (!draft || !member) return false;
    const keys = [
      'name',
      'role',
      'rate',
      'phone',
      'email',
      'hire_date',
      'emergency_contact_name',
      'emergency_contact_phone',
      'status',
      'status_effective_date',
    ];
    return keys.some((k) => String(draft[k] ?? '') !== String(member[k] ?? ''));
  }, [draft, member]);

  if (!draft) return null;

  const update = (patch) => setDraft((cur) => ({ ...cur, ...patch }));

  const handleStatusChange = (next) => {
    setDraft((cur) => applyStatusTransition(cur, next));
  };

  const handleSave = () => {
    if (!draft?.name?.trim()) return;
    onSave?.(normalizeCrewMember(draft));
  };

  const statusLabel = t(`settings.crew.status.${draft.status}`, {
    defaultValue: draft.status,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-[hsl(var(--card))] border-border overflow-y-auto"
        data-testid="employee-profile-sheet"
      >
        <SheetHeader className="pb-3">
          <SheetTitle className="flex items-center gap-2 text-foreground">
            <User size={16} className="text-[hsl(var(--primary))]" />
            {t('settings.crew.profileTitle')}
          </SheetTitle>
          <SheetDescription className="text-muted-foreground text-xs">
            {t('settings.crew.profileSubtitle')}
          </SheetDescription>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Badge
              variant="outline"
              className={cn(
                'uppercase text-[10px] tracking-[0.18em] border',
                STATUS_TONE[draft.status] || STATUS_TONE.inactive
              )}
              data-testid="employee-status-badge"
            >
              <CircleDot size={9} className="mr-1.5" />
              {statusLabel}
            </Badge>
            {draft.status_effective_date && (
              <span className="text-[11px] text-muted-foreground">
                {t('settings.crew.effectiveOn', {
                  date: draft.status_effective_date,
                })}
              </span>
            )}
          </div>
        </SheetHeader>

        <Separator className="bg-border" />

        {/* ---------- Core identity ---------- */}
        <section className="space-y-3 py-4">
          <h4 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t('settings.crew.sectionIdentity')}
          </h4>
          <div className="grid grid-cols-1 gap-3">
            <Field
              label={t('settings.crew.fields.name')}
              icon={User}
              value={draft.name}
              onChange={(v) => update({ name: v })}
              testId="employee-field-name"
              required
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={t('settings.crew.fields.role')}
                icon={Briefcase}
                value={draft.role}
                onChange={(v) => update({ role: v })}
                testId="employee-field-role"
              />
              <Field
                label={t('settings.crew.fields.rate')}
                value={draft.rate}
                type="number"
                inputMode="decimal"
                step="0.5"
                min="0"
                onChange={(v) => update({ rate: Number(v) || 0 })}
                suffix="$/hr"
                testId="employee-field-rate"
              />
            </div>
          </div>
        </section>

        {/* ---------- Contact ---------- */}
        <section className="space-y-3 pb-4">
          <h4 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t('settings.crew.sectionContact')}
          </h4>
          <div className="grid grid-cols-1 gap-3">
            <Field
              label={t('settings.crew.fields.phone')}
              icon={Phone}
              type="tel"
              inputMode="tel"
              value={draft.phone}
              onChange={(v) => update({ phone: v })}
              placeholder="(555) 555-1234"
              testId="employee-field-phone"
            />
            <Field
              label={t('settings.crew.fields.email')}
              icon={Mail}
              type="email"
              inputMode="email"
              value={draft.email}
              onChange={(v) => update({ email: v })}
              placeholder="name@example.com"
              testId="employee-field-email"
            />
          </div>
        </section>

        {/* ---------- Employment ---------- */}
        <section className="space-y-3 pb-4">
          <h4 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t('settings.crew.sectionEmployment')}
          </h4>
          <div className="grid grid-cols-1 gap-3">
            <Field
              label={t('settings.crew.fields.hire_date')}
              icon={CalendarDays}
              type="date"
              value={draft.hire_date}
              onChange={(v) => update({ hire_date: v })}
              testId="employee-field-hire-date"
            />
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('settings.crew.fields.status')}
              </Label>
              <Select value={draft.status} onValueChange={handleStatusChange}>
                <SelectTrigger
                  className="h-10 bg-[hsl(var(--background))] border-border text-foreground mt-1"
                  data-testid="employee-field-status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  {CREW_STATUSES.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`employee-status-option-${s}`}
                    >
                      {t(`settings.crew.status.${s}`, { defaultValue: s })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {draft.status !== 'active' && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  {t('settings.crew.inactiveImpact')}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* ---------- Emergency contact ---------- */}
        <section className="space-y-3 pb-4">
          <h4 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t('settings.crew.sectionEmergency')}
          </h4>
          <div className="grid grid-cols-1 gap-3">
            <Field
              label={t('settings.crew.fields.emergency_name')}
              icon={ShieldAlert}
              value={draft.emergency_contact_name}
              onChange={(v) => update({ emergency_contact_name: v })}
              testId="employee-field-emergency-name"
            />
            <Field
              label={t('settings.crew.fields.emergency_phone')}
              icon={Phone}
              type="tel"
              inputMode="tel"
              value={draft.emergency_contact_phone}
              onChange={(v) => update({ emergency_contact_phone: v })}
              placeholder="(555) 555-1234"
              testId="employee-field-emergency-phone"
            />
          </div>
        </section>

        <Separator className="bg-border" />

        {/* ---------- Records ---------- */}
        <section className="space-y-3 py-4">
          <h4 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {t('settings.crew.sectionRecords')}
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenHours?.(draft)}
              className="h-9 border-border hover:bg-[hsl(var(--muted))] text-xs uppercase tracking-[0.16em]"
              data-testid="employee-open-hours"
            >
              <Clock size={13} className="mr-1.5" />
              {t('settings.crew.openHours')}
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenWriteups?.(draft)}
              className="h-9 border-border hover:bg-[hsl(var(--muted))] text-xs uppercase tracking-[0.16em]"
              data-testid="employee-open-writeups"
            >
              <FileText size={13} className="mr-1.5" />
              {t('settings.crew.openWriteups')}
            </Button>
          </div>
        </section>

        <SheetFooter className="border-t border-border pt-3 sticky bottom-0 bg-[hsl(var(--card))] -mx-6 px-6 pb-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={saving}
            className="text-muted-foreground"
            data-testid="employee-cancel-button"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !draft.name?.trim() || !isDirty}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="employee-save-button"
          >
            {saving ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" />
            ) : (
              <Save size={13} className="mr-1.5" />
            )}
            {t('settings.crew.saveProfile')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

/* ----------------------------------------------------------------------------
 * Field — small composition primitive used inside the sheet. Keeps every
 * field consistent (label + optional icon + input + optional suffix).
 * ------------------------------------------------------------------------- */
const Field = ({
  label,
  icon: Icon,
  value,
  onChange,
  type = 'text',
  inputMode,
  step,
  min,
  placeholder,
  suffix,
  required = false,
  testId,
}) => (
  <div>
    <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
      {Icon ? <Icon size={11} /> : null}
      {label}
      {required ? <span className="text-[hsl(var(--destructive))]">*</span> : null}
    </Label>
    <div className="relative mt-1">
      <Input
        type={type}
        inputMode={inputMode}
        step={step}
        min={min}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className="h-10 bg-[hsl(var(--background))] border-border text-foreground pr-12"
        data-testid={testId}
      />
      {suffix ? (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground tabular-nums">
          {suffix}
        </span>
      ) : null}
    </div>
  </div>
);

export default EmployeeProfileSheet;
